/**
 * MetricsCard - Displays a single metric with trend
 */

import { useMemo } from 'react';

interface MetricsCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  icon?: React.ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  format?: 'number' | 'percentage' | 'duration' | 'points';
}

export default function MetricsCard({
  title,
  value,
  subtitle,
  trend,
  icon,
  color = 'primary',
  format = 'number',
}: MetricsCardProps) {
  const colorClasses = useMemo(() => {
    switch (color) {
      case 'success':
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          icon: 'text-green-500',
          border: 'border-green-200 dark:border-green-800',
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50 dark:bg-yellow-900/20',
          icon: 'text-yellow-500',
          border: 'border-yellow-200 dark:border-yellow-800',
        };
      case 'danger':
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          icon: 'text-red-500',
          border: 'border-red-200 dark:border-red-800',
        };
      case 'info':
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          icon: 'text-blue-500',
          border: 'border-blue-200 dark:border-blue-800',
        };
      default:
        return {
          bg: 'bg-primary-50 dark:bg-primary-900/20',
          icon: 'text-primary-500',
          border: 'border-primary-200 dark:border-primary-800',
        };
    }
  }, [color]);

  const formattedValue = useMemo(() => {
    if (typeof value === 'string') return value;

    switch (format) {
      case 'percentage':
        return `${value.toFixed(1)}%`;
      case 'duration':
        if (value < 1) return `${Math.round(value * 24)}h`;
        return `${value.toFixed(1)}d`;
      case 'points':
        return `${value} pts`;
      default:
        return value.toLocaleString();
    }
  }, [value, format]);

  return (
    <div className={`${colorClasses.bg} ${colorClasses.border} border rounded-lg p-4`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            {title}
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-neutral-900 dark:text-white">
              {formattedValue}
            </span>
            {trend && (
              <span
                className={`flex items-center text-sm font-medium ${
                  trend.isPositive ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {trend.isPositive ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                )}
                {Math.abs(trend.value)}%
              </span>
            )}
          </div>
          {subtitle && (
            <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              {subtitle}
            </div>
          )}
        </div>
        {icon && (
          <div className={`${colorClasses.icon}`}>
            {icon}
          </div>
        )}
      </div>
    </div>
  );
}
